import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getColorSuggestions, findClosestColorName, areColorNamesSameSpelling } from '../../utils/colorUtils';
import ColorDot from './ColorDot';
import { toast } from 'react-hot-toast';

const ColorInput = React.forwardRef(({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Renk',
  className = '',
  validationClass = '',
  required = false,
  disabled = false,
  id,
  name,
  autoFocus,
  showColorDot = true,
  ...otherProps
}, ref) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);
  const [inputStyles, setInputStyles] = useState(null);
  
  // En yakın renk önerisini hesapla (sadece ilk öneri)
  const closestSuggestion = useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) {
      return null;
    }
    const suggestions = getColorSuggestions(inputValue, 1);
    return suggestions.length > 0 ? suggestions[0] : null;
  }, [inputValue]);
  
  // Tahmin: yalnızca yazım gerçekten farklıysa (aynı kelime, farklı büyük/küçük harfte hayalet metin gösterme)
  const showSuggestion =
    closestSuggestion &&
    !areColorNamesSameSpelling(inputValue, closestSuggestion.name) &&
    inputValue.trim().length > 0;
  
  // Ref'i birleştir
  const combinedRef = useCallback((node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  
  // Input'un stil özelliklerini al
  useEffect(() => {
    if (inputRef.current && showSuggestion) {
      const computedStyle = window.getComputedStyle(inputRef.current);
      setInputStyles({
        fontSize: computedStyle.fontSize,
        fontFamily: computedStyle.fontFamily,
        fontWeight: computedStyle.fontWeight,
        lineHeight: computedStyle.lineHeight,
        letterSpacing: computedStyle.letterSpacing,
        paddingLeft: computedStyle.paddingLeft,
        paddingRight: computedStyle.paddingRight,
        paddingTop: computedStyle.paddingTop,
        paddingBottom: computedStyle.paddingBottom
      });
    }
  }, [inputValue, showSuggestion]);

  // Input değeri değiştiğinde güncelle
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Input değişikliği
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);

  // Blur işlemi - yazım hatası düzeltme
  const handleBlur = useCallback((e) => {
    const rawValue = e.target.value;
    const currentValue = rawValue.trim();

    if (currentValue && currentValue.length > 0) {
      // %80 benzerlik eşiği: Eğer girilen renk ColorUtils'teki hiçbir renkle %80'ten düşük benzerlikteyse,
      // otomatik düzeltme yapma, kullanıcının girdiği değeri olduğu gibi kabul et (özel renk)
      const closestColor = findClosestColorName(currentValue, 0.8);

      if (closestColor && areColorNamesSameSpelling(currentValue, closestColor)) {
        // Sözlükteki renkle aynı yazım (ör. LACIVERT) — büyük/küçük harfe dokunma; sadece trim gerekirse senkronla
        if (rawValue !== currentValue) {
          const syntheticEvent = {
            target: { value: currentValue, name: e.target.name }
          };
          setInputValue(currentValue);
          if (onChange) onChange(syntheticEvent);
        }
      } else if (closestColor && !areColorNamesSameSpelling(currentValue, closestColor)) {
        // Gerçek yazım düzeltmesi (ör. lacivart → Lacivert)
        const syntheticEvent = {
          target: {
            value: closestColor,
            name: e.target.name
          }
        };

        setInputValue(closestColor);
        if (onChange) {
          onChange(syntheticEvent);
        }

        toast.success(`Renk düzeltildi: "${currentValue}" → "${closestColor}"`, {
          duration: 2000,
          icon: '✓'
        });
      }
    }
    
    if (onBlur) {
      onBlur(e);
    }
  }, [onChange, onBlur]);

  // Tahmin kutusuna tıklama - tahmini seç
  const handleSuggestionClick = useCallback(() => {
    if (closestSuggestion && !areColorNamesSameSpelling(inputValue, closestSuggestion.name)) {
      const syntheticEvent = {
        target: {
          value: closestSuggestion.name,
          name: name || ''
        }
      };
      setInputValue(closestSuggestion.name);
      if (onChange) {
        onChange(syntheticEvent);
      }
      if (ref && ref.current) {
        ref.current.focus();
      }
    }
  }, [closestSuggestion, inputValue, onChange, name, ref]);

  // Enter tuşu ile tahmini seç
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && closestSuggestion && !areColorNamesSameSpelling(inputValue, closestSuggestion.name)) {
      e.preventDefault();
      handleSuggestionClick();
    } else if (e.key === 'Enter' && onBlur) {
      handleBlur(e);
    }
  }, [closestSuggestion, inputValue, handleSuggestionClick, onBlur, handleBlur]);
  
  // Input'un stil özelliklerini al
  useEffect(() => {
    if (inputRef.current && showSuggestion) {
      const computedStyle = window.getComputedStyle(inputRef.current);
      setInputStyles({
        fontSize: computedStyle.fontSize,
        fontFamily: computedStyle.fontFamily,
        fontWeight: computedStyle.fontWeight,
        lineHeight: computedStyle.lineHeight,
        letterSpacing: computedStyle.letterSpacing,
        paddingLeft: computedStyle.paddingLeft,
        paddingRight: computedStyle.paddingRight,
        paddingTop: computedStyle.paddingTop,
        paddingBottom: computedStyle.paddingBottom
      });
    }
  }, [inputValue, showSuggestion]);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={combinedRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          id={id}
          name={name}
          autoFocus={autoFocus}
          className={`${className} ${validationClass}`}
          style={{ 
            caretColor: 'currentColor',
            ...(showSuggestion && { color: 'transparent' })
          }}
          {...otherProps}
        />
        {/* Input içinde gri soluk tahmin - girilen metnin devamı */}
        {showSuggestion && inputStyles && (
          <div
            onClick={handleSuggestionClick}
            className="absolute inset-0 flex items-center pointer-events-none"
            style={{ 
              pointerEvents: 'auto',
              cursor: 'pointer',
              paddingLeft: inputStyles.paddingLeft,
              paddingRight: inputStyles.paddingRight,
              paddingTop: inputStyles.paddingTop,
              paddingBottom: inputStyles.paddingBottom,
              fontSize: inputStyles.fontSize,
              fontFamily: inputStyles.fontFamily,
              fontWeight: inputStyles.fontWeight,
              lineHeight: inputStyles.lineHeight,
              letterSpacing: inputStyles.letterSpacing
            }}
          >
            <span className="text-gray-900 dark:text-white">{inputValue}</span>
            <span className="text-gray-400 dark:text-gray-500 opacity-60">
              {closestSuggestion.name.substring(inputValue.length)}
            </span>
          </div>
        )}
        {inputValue && showColorDot && (
          <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center z-10">
            <ColorDot colorName={inputValue} size="sm" />
          </div>
        )}
      </div>
    </div>
  );
});

ColorInput.displayName = 'ColorInput';

export default React.memo(ColorInput);

